import React, { useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { isAdmin } from "utils/roles";
import { useGetCrmDeliveryAlertCountQuery } from "state/api";

const DISMISS_KEY = "phieg.crmIntegrations.alertDismissedCount";

function getDismissedCount() {
  try {
    const n = parseInt(localStorage.getItem(DISMISS_KEY), 10);
    return Number.isFinite(n) ? n : -1;
  } catch {
    return -1;
  }
}

/**
 * Admin-only banner (mirrors the PWA install prompt) shown when leads haven't
 * reached an integrated client's CRM. Clicking / "Review" opens the CRM
 * Integrations page. Dismiss hides it until MORE failures appear (it re-shows
 * when the not-delivered count climbs past the count at dismissal).
 */
export default function CrmIntegrationsAlert() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const admin = isAdmin(user);
  const [dismissedCount, setDismissedCount] = useState(getDismissedCount());

  const { data } = useGetCrmDeliveryAlertCountQuery(undefined, { skip: !admin });

  if (!admin) return null;
  const count = data?.notDelivered || 0;
  if (count === 0 || count <= dismissedCount) return null;

  const open = () => navigate("/crm-delivery");
  const dismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(DISMISS_KEY, String(count));
    } catch {
      /* noop */
    }
    setDismissedCount(count);
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 1.5 }}>
      <Alert
        severity="warning"
        variant="outlined"
        onClose={dismiss}
        onClick={open}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        sx={{ cursor: "pointer", "& .MuiAlert-message": { width: "100%" } }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ width: "100%" }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            {count === 1
              ? "1 lead hasn't reached an integrated client's CRM."
              : `${count} leads haven't reached an integrated client's CRM.`}{" "}
            Review and resend.
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
          >
            Review
          </Button>
        </Stack>
      </Alert>
    </Box>
  );
}
